/**
 * Clip list handling (PRD §17.8, C4, C5, C18, C24). The player reads the list WordPress
 * printed into the slot and never fetches a clip list at runtime.
 */

import { qaShortClips } from './qa.js';

const CLIP_ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;

/**
 * True for a host that only exists on the developer's own machine.
 * Assumption A18: loopback http is accepted so the sample page can serve local clips;
 * any real host must be https.
 * @param {URL} url
 * @returns {boolean}
 */
function isLoopback(url) {
  return url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]';
}

/**
 * Check one URL is https (or loopback) and on the configured Bunny host (C5).
 * @param {string} value
 * @param {URL} cdn parsed clips.cdnBase
 * @returns {boolean}
 */
function isAllowedMediaUrl(value, cdn) {
  let url;
  try {
    url = new URL(value);
  } catch (error) {
    return false;
  }
  const secure = url.protocol === 'https:' || (url.protocol === 'http:' && isLoopback(url));
  return secure && url.host === cdn.host;
}

/**
 * Validate one clip record (§17.8 "What the player does with the list").
 * @param {object} raw clip entry from data-qsvp-clips
 * @param {string} cdnBase config clips.cdnBase
 * @returns {{ok:true, clip:object}|{ok:false, reason:string}}
 */
export function validateClip(raw, cdnBase) {
  if (!raw || typeof raw !== 'object') return { ok: false, reason: 'not_object' };
  const id = String(raw.id || '').toLowerCase();
  if (!CLIP_ID_PATTERN.test(id)) return { ok: false, reason: 'bad_id' };
  let cdn;
  try {
    cdn = new URL(cdnBase);
  } catch (error) {
    return { ok: false, reason: 'bad_cdn_base' };
  }
  if (!isAllowedMediaUrl(raw.src, cdn)) return { ok: false, reason: 'bad_src' }; // C5
  if (!isAllowedMediaUrl(raw.poster, cdn)) return { ok: false, reason: 'bad_poster' };
  const duration = Number(raw.duration);
  // C18 / §4: clips are 40-60 s; anything else is dropped.
  // C18 / §4: clips are 40-60 s. QA build on localhost only: short test clips (>= 1 s) are accepted.
  if (!(duration >= (qaShortClips() ? 1 : 40) && duration <= 60)) return { ok: false, reason: 'bad_duration' };
  if (raw.vtt && !isAllowedMediaUrl(raw.vtt, cdn)) return { ok: false, reason: 'bad_vtt' };
  return {
    ok: true,
    clip: { id, src: raw.src, poster: raw.poster, title: String(raw.title || ''), duration, vtt: raw.vtt || '' },
  };
}

/**
 * Parse and clean a slot's data-qsvp-clips list: valid, unique, at most 3, in order.
 * @param {string} json attribute value
 * @param {string} cdnBase
 * @param {Function} [onInvalid] called with (reason, rawEntry) for each dropped entry
 * @returns {object[]}
 */
export function parseSlotClips(json, cdnBase, onInvalid = () => {}) {
  let list;
  try {
    list = JSON.parse(json || '[]');
  } catch (error) {
    onInvalid('bad_json', json);
    return [];
  }
  if (!Array.isArray(list)) {
    onInvalid('not_list', list);
    return [];
  }
  const seen = new Set();
  const clips = [];
  for (const raw of list) {
    const result = validateClip(raw, cdnBase);
    if (!result.ok) {
      onInvalid(result.reason, raw);
      continue;
    }
    if (seen.has(result.clip.id)) continue;
    seen.add(result.clip.id);
    clips.push(result.clip);
    if (clips.length === 3) break;
  }
  return clips;
}

/**
 * Resolve the playlist for a slot (§17.8, C24).
 * Slot clips first, then clips.byCategory for the slot's category, but only when full
 * clip records exist in clips.library (assumption A4: config ids alone have no src/poster).
 * Nothing on-niche -> empty list -> no video request, display waterfall.
 * @param {object} input
 * @param {object[]} input.slotClips validated slot clips
 * @param {string} input.category data-qsvp-category
 * @param {object} input.clipsConfig config.clips
 * @param {Function} [input.onInvalid]
 * @returns {object[]}
 */
export function resolvePlaylist({ slotClips, category, clipsConfig, onInvalid = () => {} }) {
  const ids = (clipsConfig.byCategory && category && clipsConfig.byCategory[category]) || [];
  const library = clipsConfig.library || {};
  const records = ids.map((id) => library[id] && Object.assign({ id }, library[id])).filter(Boolean);
  // 2.0.8: slot clips first, topped up from the category to 3, so a slot printed with one clip still gets a playlist.
  return parseSlotClips(JSON.stringify(slotClips.concat(records)), clipsConfig.cdnBase, onInvalid);
}
